import { useEffect, useMemo, useState } from 'react';
import { useRoomStream } from './room/useRoomStream';
import { LobbyScreen } from './screens/LobbyScreen';
import { QuestionScreen } from './screens/QuestionScreen';
import { RevealScreen } from './screens/RevealScreen';
import { FinishedScreen } from './screens/FinishedScreen';
import { initCastReceiver } from './cast/castReceiver';
import { usePack } from './pack/usePack';

interface Bootstrap {
  apiBase: string;
  roomId:  string;
  token:   string;
  /** Optional pack-signing key for bundle verification. */
  signKey?: string;
}

// Three sources, in priority order:
//   1) URL hash `#/roomId/token[/signKey]`  (deep link / preview / manual pair)
//   2) Cast custom-message handshake (CAF)
//   3) Polled pair-code (tvOS / Android TV shell — typed on phone)
function readHashBootstrap(): { roomId: string; token: string; signKey?: string } | null {
  const h = window.location.hash.replace(/^#\/?/, '');
  if (!h) return null;
  const [roomId, token, signKey] = h.split('/');
  if (!roomId || !token) return null;
  return { roomId, token, signKey: signKey || undefined };
}

function defaultApiBase(): string {
  // Default: same parent domain on `api.` — e.g. play.example.com → api.example.com
  const host = window.location.host;
  if (host.startsWith('play.')) return `https://api.${host.slice('play.'.length)}`;
  // Local dev fallback
  return `${window.location.protocol}//${host.replace(/:\d+$/, ':3000')}`;
}

export function App() {
  const [boot, setBoot] = useState<Bootstrap | null>(() => {
    const h = readHashBootstrap();
    return h ? { apiBase: defaultApiBase(), ...h } : null;
  });
  const apiBase = useMemo(() => boot?.apiBase ?? defaultApiBase(), [boot]);

  useEffect(() => {
    if (boot) return;
    initCastReceiver((payload) => {
      setBoot({
        apiBase,
        roomId:  payload.roomId,
        token:   payload.token,
        signKey: (payload as { signKey?: string }).signKey,
      });
    });
  }, [boot, apiBase]);

  if (!boot) return <WaitingForPair />;

  return (
    <RoomView
      apiBase={boot.apiBase}
      roomId={boot.roomId}
      token={boot.token}
      signKey={boot.signKey}
    />
  );
}

function WaitingForPair() {
  return (
    <div className="app center">
      <div className="col" style={{ alignItems: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: 96 }}>📺</div>
        <div style={{ fontSize: 36, fontWeight: 600 }}>Ready to play</div>
        <div style={{ fontSize: 22, color: 'var(--c-text-muted)', maxWidth: 600 }}>
          Open the Trivia app on your phone, create or join a room, and tap the cast
          button to send this TV your party.
        </div>
      </div>
    </div>
  );
}

function RoomView({ apiBase, roomId, token, signKey }:
  { apiBase: string; roomId: string; token: string; signKey?: string }) {
  const { snapshot, connected, error } = useRoomStream({ apiBase, roomId, token });
  const pack = usePack({
    apiBase,
    roomId,
    token,
    signKey,
    packId: snapshot.packId,
  });

  return (
    <>
      {!connected && (
        <div className="banner danger" style={{
          position: 'fixed', top: 16, right: 16, zIndex: 10,
        }}>
          <div>•</div>
          <div>{error ?? 'Connecting…'}</div>
        </div>
      )}
      {snapshot.phase === 'lobby'    && <LobbyScreen    snapshot={snapshot} />}
      {snapshot.phase === 'question' && (
        <QuestionScreen snapshot={snapshot} questionsById={pack.questionsById} />
      )}
      {snapshot.phase === 'reveal'   && (
        <RevealScreen snapshot={snapshot} questionsById={pack.questionsById} />
      )}
      {snapshot.phase === 'finished' && <FinishedScreen snapshot={snapshot} />}
    </>
  );
}
